<template>
  <div
    v-if="show"
    class="player-tag-portal"
    :style="portalStyle"
    @mouseenter="keepOpen"
    @mouseleave="requestClose"
  >
    <div class="player-tag-portal-content">
      <header class="portal-header">
        <p class="portal-title">{{ player.username }}</p>
        <button class="portal-close-button" aria-label="close" @click="close">
          <span class="icon">
            <i class="fas fa-times"></i>
          </span>
        </button>
      </header>
      <div class="portal-content">
        <ProfileCard v-if="player && player.user" :player="player.user" />

        <!-- Scores -->
        <div class="portal-section">
          <p v-if="player.score != null">Total Score: {{ player.score }}</p>
          <p v-if="player.scoreBeforeGame != null">
            Score before game: {{ player.scoreBeforeGame }}
          </p>
        </div>

        <!-- Color Pref -->
        <div class="portal-section" v-if="player.colorPref != null">
          <p>Color Pref: {{ player.colorPref }}</p>
        </div>

        <!-- Games -->
        <div class="portal-section" v-if="player.games">
          <RoundResults
            :roundsParticipated="player.roundsParticipated"
            :currentRoundNumber="roundNumber"
            :totalRounds="totalRounds"
            :username="player.username"
            :games="player.games"
          />
        </div>

        <!-- Participation -->
        <div class="portal-section">
          <b-switch
            :value="player.isParticipating"
            type="is-success"
            @input="toggleParticipate"
          >
            Participating
          </b-switch>
        </div>

        <!-- No License -->
        <div class="portal-section" v-if="player.hasNoLicense">
          <p class="has-text-danger">NO LICENSE</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProfileCard from "@/components/players/ProfileCard.vue";
import RoundResults from "@/components/tournaments/RoundResults.vue";
import { EventBus } from "@/services/event-bus";

export default {
  name: "SharedPlayerTagPortal",
  components: { ProfileCard, RoundResults },

  data() {
    return {
      show: false,
      player: {},
      totalRounds: 0,
      roundNumber: 0,
      position: { top: 0, left: 0 },
      hideTimeout: null,
    };
  },

  computed: {
    portalStyle() {
      return {
        position: "fixed",
        top: `${this.position.top - 250}px`,
        left: `${this.position.left}px`,
        zIndex: 9999,
      };
    },
  },

  methods: {
    showPortal(data) {
      // Clear any existing timeout
      clearTimeout(this.hideTimeout);

      // Update data
      this.player = data.player;
      this.totalRounds = data.totalRounds;
      this.roundNumber = data.roundNumber;
      this.position = data.position;

      // Show the portal
      this.show = true;
    },

    hidePortal() {
      this.show = false;
    },

    keepOpen() {
      // Clear any hide timeout
      clearTimeout(this.hideTimeout);
    },

    requestClose() {
      // Set a timeout to hide the portal
      this.hideTimeout = setTimeout(() => {
        this.hidePortal();
      }, 300);
    },

    close() {
      this.hidePortal();
    },

    toggleParticipate() {
      // Emit global event to toggle participation directly
      // This will be caught by AdminTournament
      this.$emit("onToggleParticipate", {
        player: { ...this.player },
        roundNumber: this.roundNumber ?? null,
      });

      // Update local state immediately for better UX
      this.player.isParticipating = !this.player.isParticipating;
    },
  },

  mounted() {
    // Append to document body
    document.body.appendChild(this.$el);

    // Listen for show event
    EventBus.$on("show-player-portal", this.showPortal);

    // Listen for global click to close
    document.addEventListener("click", (event) => {
      if (this.show && !this.$el.contains(event.target)) {
        this.hidePortal();
      }
    });
  },

  beforeDestroy() {
    // Remove from document body
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }

    // Remove event listeners
    EventBus.$off("show-player-portal", this.showPortal);
    clearTimeout(this.hideTimeout);
  },
};
</script>

<style scoped>
.player-tag-portal {
  position: fixed;
  z-index: 99;
  width: 360px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  font-size: 0.95rem;
}

.player-tag-portal-content {
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  overflow-y: auto;
}

.portal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.portal-title {
  font-weight: bold;
  margin: 0;
  font-size: 1.1rem;
  color: #363636;
}

.portal-close-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #666;
  transition: color 0.2s ease;
}

.portal-close-button:hover {
  color: #000;
}

.portal-content {
  padding: 0.5rem;
}

.portal-section {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0.5rem 0;
}
</style>
